import { Injectable } from '@angular/core';
import { NavigationEnd, Router, NavigationStart } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

export enum Mode {
  OVER = 'over',
  PUSH = 'push',
  SIDE = 'side',
}

export enum Size {
  SMALL = 'sml',
  REGULAR = 'reg',
  LARGE = 'lrg',
  FULLSCREEN = 'fullscreen',
}

export enum SizePixels {
  SMALL = 75,
  REGULAR = 275,
  LARGE = 550,
}

@Injectable({ providedIn: 'root' })
export class SidepanelService {
  originalSize: Size;
  originalMode: Mode;

  private _mode: Mode;

  private previousUrl: string;

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationStart))
      .subscribe((event: NavigationEnd | NavigationStart) => {
        if (event instanceof NavigationStart) {
          this.previousUrl = event.url.split('?')[0];
        } else if (this.previousUrl !== event.url.split('?')[0]) {
          this.visible$$.next(false);
        }
      });
  }

  private visible$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  get visible$(): Observable<boolean> {
    return this.visible$$.asObservable();
  }

  private mode$$: BehaviorSubject<Mode> = new BehaviorSubject(Mode.OVER);
  get mode$(): Observable<Mode> {
    return this.mode$$.asObservable();
  }

  private modalMode$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  get modalMode$(): Observable<boolean> {
    return this.modalMode$$.asObservable();
  }

  size$$: BehaviorSubject<Size> = new BehaviorSubject(Size.REGULAR);
  get size$(): Observable<Size> {
    return this.size$$.asObservable();
  }

  private component$$: BehaviorSubject<any> = new BehaviorSubject(null);
  get view$(): Observable<any> {
    return this.component$$.asObservable();
  }

  set view(component: any) {
    this.component$$.next(component);
  }

  public clearView(): void {
    this.component$$.next(null);
  }

  set mode(mode: Mode) {
    this._mode = mode;
    // If they wanted push or side, and the screen is small and the panel is large, then go over
    if ((mode === Mode.PUSH || mode === Mode.SIDE) && isNotLarge() && this.size$$.getValue() === Size.LARGE) {
      mode = Mode.OVER;
    }
    this.mode$$.next(mode);
  }

  set size(size: Size) {
    this.size$$.next(size);
  }

  public toggleFullscreen(): void {
    const currentSize = this.size$$.getValue();
    const currentMode = this.mode$$.getValue();
    if (!this.originalSize) {
      this.originalSize = currentSize;
    }
    if (!this.originalMode) {
      this.originalMode = currentMode;
    }
    if (currentSize === Size.FULLSCREEN) {
      this.size = this.originalSize;
      this.mode = this.originalMode;
      this.originalSize = undefined;
      this.originalMode = undefined;
    } else {
      this.size = Size.FULLSCREEN;
      this.mode = Mode.OVER;
    }
  }

  public clearFullscreen(): void {
    if (this.originalSize) {
      this.size = this.originalSize;
    }
    if (this.originalMode) {
      this.mode = this.originalMode;
    }
    this.originalSize = undefined;
    this.originalMode = undefined;
  }

  public open(): void {
    this.mode = this._mode; // Ensures mode responds to size changes
    this.visible$$.next(true);
  }

  public close(): void {
    this.originalSize = undefined;
    this.visible$$.next(false);
  }

  public toggle(): void {
    this.mode = this._mode; // Ensures mode responds to size changes
    this.visible$$.next(!this.visible$$.getValue());
  }

  public setView(size: Size, mode: Mode, component: any, modalMode = true): void {
    this.size = size;
    this.mode = mode;
    this.view = component;
    this.modalMode$$.next(modalMode);
  }

  public setViewThenOpen(size: Size, mode: Mode, component: any, modalMode = true): void {
    this.setView(size, mode, component, modalMode);
    this.open();
  }

  public getPixels(): number {
    switch (this.size$$.getValue()) {
      case Size.LARGE:
        return SizePixels.LARGE;
      case Size.REGULAR:
        return SizePixels.REGULAR;
      case Size.SMALL:
        return SizePixels.SMALL;
    }
    return 0;
  }
}

export function isNotLarge(): boolean {
  return window.matchMedia('(max-width: 1280px)').matches;
}
